import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import type { AppLocale } from '@/lib/i18n'

import {
  profileSocialApi,
  type PrivateProfile,
  type UpdateProfileRequest,
} from '../api'
import { currentCoordinates, geolocationErrorKey } from '../geolocation'
import { Modal } from './modal'

const NICKNAME_PATTERN = /^[a-zA-Z0-9_]+$/

interface EditProfileSheetProps {
  profile: PrivateProfile
  busy: boolean
  errorKey: string | null
  onSave: (body: UpdateProfileRequest) => void
  onClose: () => void
}

interface FieldErrors {
  nickname?: string
  firstName?: string
  lastName?: string
  city?: string
  bio?: string
  languages?: string
  interests?: string
}

interface LocationNotice {
  tone: 'success' | 'error'
  key: string
  city?: string
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function csv(values: string[]): string {
  return values.join(', ')
}

/**
 * Profile edit sheet: nickname, real name, city, bio, languages, interests and
 * the app locale. City remains free text, with an explicit "use current
 * location" action that resolves the city through the backend reverse
 * geocoder (D-043) instead of calling a third-party service from the client.
 */
export function EditProfileSheet({
  profile,
  busy,
  errorKey,
  onSave,
  onClose,
}: EditProfileSheetProps) {
  const { t } = useTranslation()
  const formId = useId()
  const [firstName, setFirstName] = useState(profile.firstName)
  const [lastName, setLastName] = useState(profile.lastName)
  const [nickname, setNickname] = useState(profile.nickname)
  const [city, setCity] = useState(profile.city ?? '')
  const [bio, setBio] = useState(profile.bio ?? '')
  const [languages, setLanguages] = useState(csv(profile.languages))
  const [interests, setInterests] = useState(csv(profile.interests))
  const [locale] = useState<AppLocale>(profile.locale)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [locating, setLocating] = useState(false)
  const [locationNotice, setLocationNotice] =
    useState<LocationNotice | null>(null)

  function validate(): UpdateProfileRequest | null {
    const next: FieldErrors = {}
    const trimmedNickname = nickname.trim()
    if (!trimmedNickname) {
      next.nickname = 'profileSocial.profile.editErrors.nicknameRequired'
    } else if (
      trimmedNickname.length < 3 ||
      trimmedNickname.length > 24 ||
      !NICKNAME_PATTERN.test(trimmedNickname)
    ) {
      next.nickname = 'profileSocial.profile.editErrors.nicknameInvalid'
    }
    if (!firstName.trim()) {
      next.firstName = 'profileSocial.profile.editErrors.firstNameRequired'
    }
    if (!lastName.trim()) {
      next.lastName = 'profileSocial.profile.editErrors.lastNameRequired'
    }
    if (city.trim().length > 120) {
      next.city = 'profileSocial.profile.editErrors.cityTooLong'
    }
    if (bio.trim().length > 1000) {
      next.bio = 'profileSocial.profile.editErrors.bioTooLong'
    }
    const languageList = splitList(languages)
    if (languageList.length > 10) {
      next.languages = 'profileSocial.profile.editErrors.languagesTooMany'
    }
    const interestList = splitList(interests)
    if (interestList.length > 20) {
      next.interests = 'profileSocial.profile.editErrors.interestsTooMany'
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return null
    return {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      nickname: trimmedNickname,
      city: city.trim(),
      bio: bio.trim(),
      languages: languageList,
      interests: interestList,
      locale,
    }
  }

  function submit() {
    const body = validate()
    if (!body) return
    onSave(body)
  }

  async function applyCurrentLocation() {
    if (locating) return
    setLocating(true)
    setLocationNotice(null)
    try {
      const coordinates = await currentCoordinates()
      const response = await profileSocialApi.reverseGeocode(
        coordinates.latitude,
        coordinates.longitude,
      )
      const resolvedCity = response.city?.trim()
      if (!resolvedCity) {
        setLocationNotice({
          tone: 'error',
          key: 'profileSocial.profile.location.errors.unavailable',
        })
        return
      }
      setCity(resolvedCity)
      setErrors((current) => ({ ...current, city: undefined }))
      setLocationNotice({
        tone: 'success',
        key: 'profileSocial.profile.location.found',
        city: resolvedCity,
      })
    } catch (error) {
      setLocationNotice({
        tone: 'error',
        key:
          error instanceof Error && error.name === 'GeolocationError'
            ? geolocationErrorKey(error)
            : friendlyErrorKey(error),
      })
    } finally {
      setLocating(false)
    }
  }

  const errorText = (key?: string) =>
    key ? (
      <p role="alert" className="mt-1 text-sm font-semibold text-danger">
        {t(key)}
      </p>
    ) : null

  return (
    <Modal
      open
      title={t('profileSocial.profile.editTitle')}
      onClose={onClose}
      closeDisabled={busy}
      size="wide"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="btn-secondary min-h-11 px-4 text-sm"
            disabled={busy}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form={formId}
            className="btn-primary min-h-11 px-4 text-sm"
            disabled={busy}
            aria-busy={busy}
          >
            {busy
              ? t('profileSocial.profile.saving')
              : t('profileSocial.profile.save')}
          </button>
        </div>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor={`${formId}-first`} className="field-label">
              {t('profileSocial.profile.fields.firstName')}
            </label>
            <input
              id={`${formId}-first`}
              className="field-input"
              autoComplete="given-name"
              maxLength={80}
              value={firstName}
              aria-invalid={Boolean(errors.firstName) || undefined}
              aria-describedby={
                errors.firstName ? `${formId}-first-error` : undefined
              }
              onChange={(event) => setFirstName(event.target.value)}
            />
            {errors.firstName ? (
              <div id={`${formId}-first-error`}>{errorText(errors.firstName)}</div>
            ) : null}
          </div>
          <div>
            <label htmlFor={`${formId}-last`} className="field-label">
              {t('profileSocial.profile.fields.lastName')}
            </label>
            <input
              id={`${formId}-last`}
              className="field-input"
              autoComplete="family-name"
              maxLength={80}
              value={lastName}
              aria-invalid={Boolean(errors.lastName) || undefined}
              aria-describedby={
                errors.lastName ? `${formId}-last-error` : undefined
              }
              onChange={(event) => setLastName(event.target.value)}
            />
            {errors.lastName ? (
              <div id={`${formId}-last-error`}>{errorText(errors.lastName)}</div>
            ) : null}
          </div>
        </div>

        <div>
          <label htmlFor={`${formId}-nickname`} className="field-label">
            {t('profileSocial.profile.fields.nickname')}
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 font-semibold text-muted"
            >
              @
            </span>
            <input
              id={`${formId}-nickname`}
              className="field-input pl-8"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={24}
              value={nickname}
              aria-invalid={Boolean(errors.nickname) || undefined}
              aria-describedby={`${formId}-nickname-hint${
                errors.nickname ? ` ${formId}-nickname-error` : ''
              }`}
              onChange={(event) => setNickname(event.target.value)}
            />
          </div>
          <p id={`${formId}-nickname-hint`} className="mt-1 text-xs text-muted">
            {t('profileSocial.profile.fields.nicknameHint')}
          </p>
          {errors.nickname ? (
            <div id={`${formId}-nickname-error`}>
              {errorText(errors.nickname)}
            </div>
          ) : null}
        </div>

        <div>
          <label htmlFor={`${formId}-city`} className="field-label">
            {t('profileSocial.profile.fields.city')}
          </label>
          <input
            id={`${formId}-city`}
            className="field-input"
            autoComplete="address-level2"
            maxLength={120}
            value={city}
            aria-invalid={Boolean(errors.city) || undefined}
            aria-describedby={
              errors.city ? `${formId}-city-error` : undefined
            }
            onChange={(event) => setCity(event.target.value)}
          />
          <button
            type="button"
            className="btn-ghost mt-1 min-h-11 justify-start px-1 text-sm"
            disabled={locating}
            aria-busy={locating}
            onClick={() => void applyCurrentLocation()}
          >
            {locating
              ? t('profileSocial.profile.location.locating')
              : t('profileSocial.profile.location.useCurrent')}
          </button>
          {locationNotice ? (
            <p
              role={locationNotice.tone === 'error' ? 'alert' : 'status'}
              className={`mt-1 text-sm font-semibold ${
                locationNotice.tone === 'error' ? 'text-danger' : 'text-teal'
              }`}
            >
              {t(locationNotice.key, { city: locationNotice.city })}
            </p>
          ) : null}
          {errors.city ? (
            <div id={`${formId}-city-error`}>{errorText(errors.city)}</div>
          ) : null}
        </div>

        <div>
          <label htmlFor={`${formId}-bio`} className="field-label">
            {t('profileSocial.profile.fields.bio')}
          </label>
          <textarea
            id={`${formId}-bio`}
            className="field-input min-h-24"
            rows={3}
            maxLength={1000}
            value={bio}
            aria-invalid={Boolean(errors.bio) || undefined}
            aria-describedby={errors.bio ? `${formId}-bio-error` : undefined}
            onChange={(event) => setBio(event.target.value)}
          />
          {errors.bio ? (
            <div id={`${formId}-bio-error`}>{errorText(errors.bio)}</div>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor={`${formId}-languages`} className="field-label">
              {t('profileSocial.profile.fields.languages')}
            </label>
            <input
              id={`${formId}-languages`}
              className="field-input"
              value={languages}
              maxLength={300}
              aria-invalid={Boolean(errors.languages) || undefined}
              aria-describedby={`${formId}-languages-hint${
                errors.languages ? ` ${formId}-languages-error` : ''
              }`}
              onChange={(event) => setLanguages(event.target.value)}
            />
            <p
              id={`${formId}-languages-hint`}
              className="mt-1 text-xs text-muted"
            >
              {t('profileSocial.profile.fields.languagesHint')}
            </p>
            {errors.languages ? (
              <div id={`${formId}-languages-error`}>
                {errorText(errors.languages)}
              </div>
            ) : null}
          </div>
          <div>
            <label htmlFor={`${formId}-interests`} className="field-label">
              {t('profileSocial.profile.fields.interests')}
            </label>
            <input
              id={`${formId}-interests`}
              className="field-input"
              value={interests}
              maxLength={600}
              aria-invalid={Boolean(errors.interests) || undefined}
              aria-describedby={`${formId}-interests-hint${
                errors.interests ? ` ${formId}-interests-error` : ''
              }`}
              onChange={(event) => setInterests(event.target.value)}
            />
            <p
              id={`${formId}-interests-hint`}
              className="mt-1 text-xs text-muted"
            >
              {t('profileSocial.profile.fields.interestsHint')}
            </p>
            {errors.interests ? (
              <div id={`${formId}-interests-error`}>
                {errorText(errors.interests)}
              </div>
            ) : null}
          </div>
        </div>

        {errorKey ? (
          <p role="alert" className="text-sm font-semibold text-danger">
            {t(errorKey)}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
